/**
 * Custom hook to fetch page content from the CMS API
 * Returns content for a specific page, with loading and error states
 */
import { useState, useEffect } from 'react';
import { fetchWithRetry } from '@/utils/fetchWithRetry';

const API_URL = window.location.origin;

/**
 * Hook to fetch CMS content for a page
 * @param {string} pageSlug - The page identifier (e.g., 'about', 'services', 'contact')
 * @returns {{ content: object, loading: boolean, error: string|null, refetch: function }}
 */
export const usePageContent = (pageSlug) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = async () => {
    if (!pageSlug) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const res = await fetchWithRetry(`${API_URL}/api/cms/pages/public/${pageSlug}`, {}, { label: `page-${pageSlug}` });

    if (res && res.ok) {
      try {
        const data = await res.json();
        setContent(data.content || {});
      } catch (err) {
        setError(err.message);
        setContent({});
      }
    } else if (res && res.status === 404) {
      // Page not in CMS yet - not an error, just use fallback content
      setContent({});
    } else {
      // Network failure or non-2xx status — fetchWithRetry has already logged; use fallback
      setContent({});
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContent();
  }, [pageSlug]);

  return { content, loading, error, refetch: fetchContent };
};

/**
 * Helper to safely get nested content with fallback
 * @param {object} content - The CMS content object
 * @param {string} section - Section name (e.g., 'hero', 'stats')
 * @param {any} fallback - Fallback value if section doesn't exist
 */
export const getSection = (content, section, fallback = {}) => {
  return content?.[section] || fallback;
};

/**
 * Helper to safely get a specific field from a section
 * @param {object} content - The CMS content object  
 * @param {string} section - Section name
 * @param {string} field - Field name within the section
 * @param {any} fallback - Fallback value
 */
export const getSectionField = (content, section, field, fallback = '') => {
  return content?.[section]?.[field] ?? fallback;
};

/**
 * Deep-merge helper: CMS override takes precedence over fallback.
 * Arrays are REPLACED (not merged); objects are merged recursively;
 * null/undefined override values are skipped.
 */
export const deepMergeContent = (fallback, override) => {
  if (override === null || override === undefined) return fallback;
  if (typeof override !== 'object') return override;
  if (Array.isArray(override)) return override;
  if (fallback === null || fallback === undefined || typeof fallback !== 'object' || Array.isArray(fallback)) {
    return override;
  }
  const out = { ...fallback };
  for (const key of Object.keys(override)) {
    const ov = override[key];
    if (ov === null || ov === undefined) continue;
    if (typeof ov === 'object' && !Array.isArray(ov) && fallback[key] && typeof fallback[key] === 'object' && !Array.isArray(fallback[key])) {
      out[key] = deepMergeContent(fallback[key], ov);
    } else {
      out[key] = ov;
    }
  }
  return out;
};

/**
 * Convenience hook combining fetch + deep-merge with a fallback object.
 * Returns the merged content directly. Use when the page has a hardcoded
 * fallback shape that should be deep-merged with the CMS override.
 *
 * @param {string} slug - CMS page slug (e.g., 'destination-usa', 'events-list')
 * @param {object} fallback - Default content object to merge with
 * @returns {{ content: object, loading: boolean, error: string|null, refetch: function }}
 */
export const useCmsContent = (slug, fallback = {}) => {
  const { content: override, loading, error, refetch } = usePageContent(slug);
  const merged = deepMergeContent(fallback, override);
  return { content: merged, loading, error, refetch };
};

export default usePageContent;
